import { Component, Injector, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    AnnualReportNavigationProvider,
    AnnualReportProvider,
    SortingItemsHelper,
    assertPersistableFinStatements,
} from '@modeso/aumico-lib-annualreport-fe';
import { AumGlobalAction, AumicoCoreLayoutProvider, IAumGlobalActionEvent, getChangedKeys, EMPTY_ARRAY_LENGTH } from '@modeso/aumico-lib-core-fe';
import { AnnualReportStep, IAnnualReport, IFinStatements } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActionsSubject } from '@ngrx/store';
import Debug from 'debug';
import { cloneDeep } from 'lodash-es';
import { filter, find } from 'rxjs';
import { Constants } from '../../../../../aumico-lib-core-fe/src/lib/constants/constants';
import { AnnualReportOrchestrator } from '../../orchestrator/annualreport.orchestrator';
import { InvitationBasePage } from '../../pages/shared-pages/invitation-base.page';
import { NAVIGATION_TABS_MAP } from './annual-report-navigation-tabs.constant';
import { ICompanyDTO } from '@modeso/types__aumico-lib-companies-be';
import { CompaniesProvider } from '@modeso/aumico-lib-companies-fe';

const debug = Debug('aumico:saas:AnnualReportSubPage');

@UntilDestroy()
@Component({
    selector: 'aumico-annual-report-sub-page',
    template: '<html></html>',
})
export class AnnualReportSubPage extends InvitationBasePage implements OnInit {
    protected annualReportNavigationProvider: AnnualReportNavigationProvider;

    protected orchestratorOfAnnualReports: AnnualReportOrchestrator;
    activeAnnualReportEncryptedId: string | undefined;

    protected currentAnnualReportStep: AnnualReportStep | undefined;
    protected companiesProvider: CompaniesProvider;
    protected updatedCompany: ICompanyDTO | undefined;
    private savedCompany: ICompanyDTO | undefined;
    protected activeReport: IAnnualReport | undefined;

    nextStepPrefix = Constants.ACTION_BAR.PREFIXES.PROCEED_TO_KEY;
    nextStepLabel = 'aumico_button_save';
    nextStepPath: string | undefined;
    previousStepPrefix = Constants.ACTION_BAR.PREFIXES.GO_BACK_TO_KEY;
    previousStepLabel = 'aumico_button_save';
    previousStepPath: string | undefined;
    previousStepIcon = Constants.ACTION_BAR.ICONS.ARROW_LEFT;
    showUpdateCompanyBanner = false;

    constructor(
        injector: Injector,
        protected override actionsListener$: ActionsSubject,
        protected override aumicoCoreLayoutProvider: AumicoCoreLayoutProvider,
        dialog?: MatDialog,
    ) {
        super(injector, dialog);
        this.providerOfAnnualReports = injector.get(AnnualReportProvider);
        this.annualReportNavigationProvider = injector.get(AnnualReportNavigationProvider);
        this.orchestratorOfAnnualReports = injector.get(AnnualReportOrchestrator);
        this.companiesProvider = injector.get(CompaniesProvider);
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.annualReportNavigationProvider
            .getNextNavigation$(this.currentAnnualReportStep)
            .pipe(
                filter((nav) => nav != null),
                untilDestroyed(this),
            )
            .subscribe((navigationStep) => {
                const nextNavigationDetails = NAVIGATION_TABS_MAP.get(navigationStep);
                this.nextStepLabel = nextNavigationDetails.tabLabelKey;
                this.nextStepPath = nextNavigationDetails.routerLink;
            });

        this.annualReportNavigationProvider
            .getPreviousNavigation$(this.currentAnnualReportStep)
            .pipe(
                filter((nav) => nav != null),
                untilDestroyed(this),
            )
            .subscribe((navigationStep) => {
                const previousNavigationDetails = NAVIGATION_TABS_MAP.get(navigationStep);
                this.previousStepLabel = previousNavigationDetails.tabLabelKey;
                this.previousStepPath = previousNavigationDetails.routerLink;
            });

        this.providerOfAnnualReports
            .getActiveAnnualReportEncryptedId$()
            .pipe(
                find((activeAnnualReportEncryptedId) => activeAnnualReportEncryptedId != null),
                untilDestroyed(this),
            )
            .subscribe(
                (activeAnnualReportEncryptedId) => (this.activeAnnualReportEncryptedId = activeAnnualReportEncryptedId),
            );
        
        this.companiesProvider
            .getSelectedCompanyById$()
            .pipe(
                filter((company) => company != null), untilDestroyed(this)
            )
            .subscribe((company) => {
                this.updatedCompany = company;
                this.showUpdateCompanyBanner = this.isCompanyInformationOutdated(this.savedCompany, this.updatedCompany);
        });

        this.providerOfAnnualReports
            .getActiveAnnualReport$()
            .pipe(
                filter((report) => report != null), untilDestroyed(this)
            )
            .subscribe((report) => {
                this.activeReport = report;
                this.savedCompany = report.generalInformation?.company ?? report.setupData?.company;
                this.showUpdateCompanyBanner = this.isCompanyInformationOutdated(this.savedCompany, this.updatedCompany);
        });

    }

    hasReadPermission(): boolean {
        return true;
    }

    hasWritePermission(): boolean {
        return true;
    }

    /*** ActionHeaderService END ***/
    protected override onAction(event: IAumGlobalActionEvent) {
        debug('actionHeaderService.onActionEvent$', event);

        if (event && event.action === AumGlobalAction.NEW) {
            // Example
        }

        if (event && event.action === AumGlobalAction.GOBACK) {
            this.providerOfAnnualReports.flush();
            this.router.navigate(['../overview'], { relativeTo: this.activatedRoute });
        }

        if (event && event.action === AumGlobalAction.SEARCH) {
            // Example
        }
        super.onAction(event);
    }

    protected dispatchSaveAnnualReport(annualReport: IAnnualReport) {
        if (annualReport == null) {
            this.fireErrorMessage('aumico_annual_report_save_error_not_loaded');
            return;
        }
        if (this.activeAnnualReportEncryptedId == null) {
            throw new Error('activeAnnualReportEncryptedId is undefined, annual report can not be updated');
        }
        // REV-20899 save-side isolation: never persist a bridged (display-only)
        // reclass view. Throws if a caller passes a report sourced from the
        // *WithReclasses$ selectors instead of the raw getActiveAnnualReport$.
        assertPersistableFinStatements(annualReport.finStatements);
        const annualReportToUse = cloneDeep<IAnnualReport>(annualReport);
        const annualReportToBeSaved = SortingItemsHelper.prepareReportForSaving(annualReportToUse);
        this.providerOfAnnualReports.dispatchUpdateAnnualReport(
            annualReportToBeSaved,
            this.activeAnnualReportEncryptedId,
        );
    }

    protected enforceGlobalStateUpdate(annualReport: IAnnualReport) {
        const annualReportToUse = cloneDeep<IAnnualReport>(annualReport);
        const annualReportToBeSet = SortingItemsHelper.prepareReportForSaving(annualReportToUse);
        this.providerOfAnnualReports.dispatchEnforceStateUpdateForAnnualReport(
            annualReportToBeSet,
        );
    }

    protected dispatchFinStatementChanges(finStatements: IFinStatements[]) {
        if (this.activeAnnualReportEncryptedId == null) {
            throw new Error(
                'activeAnnualReportEncryptedId is undefined, fin statements of annual report can not be updated',
            );
        }
        const finStatementsToUse = cloneDeep<IAnnualReport>(finStatements);
        const finStatementsToBeSaved = SortingItemsHelper.prepareFinStatementsForSaving(finStatementsToUse);
        this.providerOfAnnualReports.dispatchUpdateFinStatements(
            finStatementsToBeSaved,
            this.activeAnnualReportEncryptedId,
        );
    }

    onUpdateCompanyInformationButtonClick(): void {
        if (this.updatedCompany == null) {
            return;
        }

        this.applyCompanyUpdate(this.updatedCompany);
        this.savedCompany = this.updatedCompany;
        this.showUpdateCompanyBanner = this.isCompanyInformationOutdated(this.savedCompany, this.updatedCompany);
    }

    protected applyCompanyUpdate(company: ICompanyDTO): void { 
        if (this.activeReport == null) {
            return;
        }
        const report = cloneDeep(this.activeReport);
        if (report.generalInformation != null) {
            report.generalInformation.company = company;
        }
        if (report.setupData != null) {
            report.setupData.company = company;
        }
        this.dispatchSaveAnnualReport(report);
        this.enforceGlobalStateUpdate(report);
    }

    protected isCompanyInformationOutdated(savedCompany: ICompanyDTO | undefined, updatedCompany: ICompanyDTO): boolean {
        if (savedCompany == null || updatedCompany == null) {
            return false;
        }
        if (savedCompany.legalform?._id !== updatedCompany.legalform?._id) {
            return false;
        }
        const changedKeys = getChangedKeys(updatedCompany, savedCompany)
            .filter((key) => key !== 'legalform');
        return changedKeys.length !== EMPTY_ARRAY_LENGTH;
    }

}
